import React, { useRef, useState, useCallback } from 'react';
import { useLexicon } from '../../hooks/useLexicon';
import ShareIcon from '@mui/icons-material/Share';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CardContent from '../CardContent';
import { useQuery } from '@tanstack/react-query';
import {
  getAssignmentById,
  getSubmissionsByAssignmentId,
} from '../../service';
import { buildUploadCandidates } from './uploadStudents';
import { taskLink as getTaskLink } from './taskLink';
import { useTaskFeedbackProgress } from '../../hooks/useTaskFeedbackProgress';
import grayMessagesIcon from '../../static/img/gray_messages.svg';
import {
  AnchorTag,
  BubbleContainer,
  Button,
  Buttons1,
  DeleteButtonContainer,
  DeleteButtonContainerOnly,
  Frame12191,
  IconContainer,
  SLink,
  StyledCard,
  TagsWrapper,
  TaskTitle,
  TaskTitleBold,
  Header,
  ClassTitle,
  FavouriteContainer,
  FavouriteContent,
  StyledCardMain,
  TaskLink,
  StyledListItem,
  ActionButtonsContainer,
  ActionButton,
  GeneratingFeedbackContainer,
} from './style';
import { updateAssignment, publishAssignment } from '../../service';
import { getUserId, getUserRole } from '../../userLocalDetails';
import StatusBubbleContainer from '../StatusBubblesContainer';
import BorderedHeart from '../../static/img/Addtofav.svg';
import RedBgHeart from '../../static/img/favTick.svg';
import CopyIcon from '../../static/img/Copy.svg';
import bulkUploadIcon from '../../static/icons/purple-bulk-upload.svg';
import ProgressBar from '../ProgressBar';
import {
  isShowChangeDueTime,
  isShowDeleteOption,
  isShowCloseTaskOption,
  isShowProgressBar,
  isShowShareOption,
  isShowRubricOutcomes,
  isShowChangePublishTime,
  isShowPublishNow,
  isShowCopyLink,
  isShowUploadResponses,
} from './rules';
import LinkButton from '../../components2/LinkButton';
import arrowRight from '../../static/img/arrowright.svg';
import whiteArrowright from '../../static/img/arrowright-White.svg';
import {
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import GeneralPopup from '../GeneralPopup';
import Toast from '../Toast';
import { getRelativeTime } from '../../dates';
import ShareResourcePopup from '../../components2/ShareResourcePopup';
import ActionPopup from '../../components2/ActionPopup';
import { useClasses, useClassData } from '../state/hooks';
import { useSubjects } from '../../hooks/useSubjects';

function TaskCard(props) {
  const { t } = useLexicon();
  const { task } = props;
  const [showMoreOptions, setShowMoreOptions] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [uploadResponsesAnchorEl, setUploadResponsesAnchorEl] = React.useState(null);
  const [showPublishPopup, setShowPublishPopup] = useState(false);
  const publishPending = useRef(false);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(null);
  const { data: classesFromApi } = useClasses();
  const { rawSubjects: apiSubjects } = useSubjects();
  const { data: allClassesWithStudents = [], isLoadingdata: loadingClasses, isError: classesError } = useClassData();
  const rosterUnavailable = classesError || allClassesWithStudents.degraded;

  // Fetch upload candidates only when the upload responses menu is open
  const isUploadMenuOpen = Boolean(uploadResponsesAnchorEl);
  const {
    data: uploadCandidatesData,
    isInitialLoading: loadingStudents,
    isError: studentsError,
  } = useQuery({
    queryKey: ['uploadCandidates', task?.id],
    queryFn: async () => {
      const [assignment, submissions] = await Promise.all([
        getAssignmentById(task.id),
        getSubmissionsByAssignmentId(task.id).catch(() => []),
      ]);
      return buildUploadCandidates({ assignment, allClasses: allClassesWithStudents, submissions });
    },
    enabled: isUploadMenuOpen && Boolean(task?.id) && !loadingClasses && !rosterUnavailable,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });

  const students = uploadCandidatesData || [];

  // Fix for P2P submission count: Fetch actual submissions for P2P tasks
  // Backend doesn't correctly count P2P submissions, so we calculate manually
  const isP2PTask = task?.reviewedBy === 'P2P';
  const {
    data: p2pSubmissions,
  } = useQuery({
    queryKey: ['p2pSubmissionCount', task?.id],
    queryFn: async () => {
      const submissions = await getSubmissionsByAssignmentId(task.id);
      return submissions || [];
    },
    enabled: isP2PTask && Boolean(task?.id) && task?.status === 'PUBLISHED',
    staleTime: 30 * 1000, // 30 seconds - refresh frequently to show updated counts
    cacheTime: 2 * 60 * 1000, // 2 minutes
  });

  // Calculate actual submission count for P2P tasks
  const actualSubmissionCount = isP2PTask && p2pSubmissions
    ? p2pSubmissions.filter(s => s?.status === 'SUBMITTED' || s?.status === 'MARKED').length
    : task.submissionCount;

  // One batched summaries read for the whole list; see useTaskFeedbackProgress.
  const feedbackProgress = useTaskFeedbackProgress(task?.id, {
    enabled: Boolean(task?.id) && task?.status === 'PUBLISHED' && actualSubmissionCount > 0,
  });
  const isFeedbackGenerating = feedbackProgress.isActive;
  const feedbackProgressPercentage = isFeedbackGenerating
    ? Math.round(feedbackProgress.progress * 100)
    : 0;

  // Build map from subject code to name for display
  const subjectCodeToName = React.useMemo(() => {
    const map = new Map();
    (apiSubjects || []).forEach((s) => {
      const code = s.subjectCode || s.code;
      const name = s.subjectName || s.title || s.name || '';
      if (code) map.set(code, name || code);
    });
    return map;
  }, [apiSubjects]);

  const showPublishPopuphandler = () => {
    if (isAssignmentValid()) {
      setShowPublishPopup(true);
    }
  };

  const publish = async () => {
    if (publishPending.current || !isAssignmentValid()) return;
    publishPending.current = true;
    setShowPublishPopup(false);
    try {
      await updateAssignment(task.id, task, { suppressToast: true });
      const res = await publishAssignment(task.id);
      if (res?.status !== 'PUBLISHED') throw new Error('Task publication failed');
      const taskId = res.id || task.id;
      const taskLink = taskId ? `/tasks/${taskId}` : undefined;
      toast(<Toast message={'Task published'} link={taskLink} />);
      window.location.reload();
    } catch (error) {
      if (!error.authHandled) toast(<Toast message="Task publication failed. Please try again." />);
    } finally {
      publishPending.current = false;
    }
  };

  const isAssignmentValid = () => true;

  const {
    taskLink,
    small,
    exemplar,
    isSelected,
    showDeletePopuphandler,
    showClosePopupHandler,
    showDateExtendPopuphandler,
    showSchedulePublishPopupHandler,
    showAssignmentStatusPopupHandler,
    onExemplarAccept,
    onExemplarDecline,
    onAccept,
    onDecline,
    showAddToCard = false,
    showThreeDots = true,
    onAddToBookmark = () => {},
    onRemoveFromBookmark = () => {},
    notification = false,
    assignmentIdForDuplicate,
    classData,
  } = props;
  const role = getUserRole();

  const refContainer = useRef(null);
  const isFavouriteFn = (task, user) => {
    return (task?.bookmarkedByStudents || []).includes(user);
  };
  const isFavourite = isFavouriteFn(task, getUserId());


  const saveButtons = (id) => {
    return (
      <Frame12191>
        <SLink onClick={(e) => onExemplarDecline(id)}>No</SLink>
        <Buttons1>
          <Button onClick={(_) => onExemplarAccept(id)}>Yes</Button>
        </Buttons1>
      </Frame12191>
    );
  };
  function createTaskCard(task, refContainer, isSelected, exemplar) {
    if (exemplar) {
      if (task.status === 'AWAITING_APPROVAL') {
        return (
          <StyledCardMain
            ref={refContainer}
            isSelected={isSelected}
            style={{
              borderTop: 'none',
              background: 'white',
            }}
          >
            <TaskTitle>
              Congratulations, {task.reviewerName} has marked part of your
              response as exemplary and would like to share with students from
              test class!
            </TaskTitle>
            <TaskTitleBold>
              {task.assignmentTitle}
            </TaskTitleBold>
            {styledCardWithLink()}
            {saveButtons(task.id)}
          </StyledCardMain>
        );
      }
    }
    return styledCardWithLink();
  }

  function getScheduledChipText(scheduledDateString) {
    return ' Scheduled: ' + getRelativeTime(scheduledDateString);
  }

  const cardHref = getTaskLink(task, role);

  const handleCardClick = (event) => {
    // Controls such as Read more act within the card. Leave every other click
    // to the anchor, including keyboard activation and modified/new-tab clicks.
    if (event.target.closest('button, [role="button"]')) {
      event.preventDefault();
    }
  };

  function styledCardWithLink() {
    if (onAccept || exemplar || notification) {
      return styledCard();
    }

    return (
      <AnchorTag
        style={{ width: '100%', cursor: 'pointer' }}
        href={cardHref}
        onClick={handleCardClick}
      >
        {styledCard()}
      </AnchorTag>
    );
  }
  const isOverdue = () => {
    const dueDate = new Date(task.dueAt);
    const currentTime = new Date();
    if (getUserRole() === 'STUDENT') {
      return (
        (task.progressStatus === 'DRAFT' ||
          task.progressStatus === 'ASSIGNED') &&
        dueDate < currentTime
      );
    }

    return (
      (task.submissionStatus === 'DRAFT' || task.status === 'PUBLISHED') &&
      dueDate < currentTime
    );
  };
  function styledCard() {
    const dueDate = new Date(task.dueAt);
    const currentTime = new Date();
    const isOverDue = isOverdue();
    const scheduled =
      task.status === 'SCHEDULED' && task.publishScheduledAt !== null;
    return (
      <StyledCard
        ref={refContainer}
        isSelected={isSelected}
        overdue={isOverDue}
        scheduled={scheduled}
        exemplar={exemplar}
        notification={notification}
      >
        {isFeedbackGenerating && (
          <GeneratingFeedbackContainer>
            <ProgressBar
              title={'Generating feedback'}
              isPercentage={true}
              count={feedbackProgressPercentage}
              total={100}
              icon={grayMessagesIcon}
              customTitleClass="generating-feedback-title"
            />
          </GeneratingFeedbackContainer>
        )}
        {showAddToCard ? (
          <Header>
            <ClassTitle>{task.assignmentTitle}</ClassTitle>
            <FavouriteContainer>
              {isFavourite ? (
                <FavouriteContent
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemoveFromBookmark(task.id);
                  }}
                  favourite={true}
                >
                  <img src={RedBgHeart} />
                  {t('Favourite')}
                </FavouriteContent>
              ) : (
                <FavouriteContent
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAddToBookmark(task.id);
                  }}
                >
                  <img src={BorderedHeart} />
                  {t('Add to favourites')}
                </FavouriteContent>
              )}
            </FavouriteContainer>
          </Header>
        ) : exemplar ? (
          tagsFrameExempler(task, isOverDue)
        ) : (
          tagsFrame(task, isOverDue, scheduled)
        )}
        <CardContent
          task={cardContents(task, exemplar)}
          small={small}
          exemplar={exemplar}
          onAccept={onAccept}
          onDecline={onDecline}
          notification={notification}
          teacherId={task.teacherId}
          teacherName={task.teacherName}
        />
        {notification && (
          <LinkButton
            link={task.link}
            label="View details"
            arrowright={arrowRight}
            whiteArrowright={whiteArrowright}
            notification={notification}
          />
        )}
        {renderActionButtons()}
        {/* {!exemplar && (
          <TaskLink href={task.link}>
            Open Task
            <img src={arrowRight} width="20px" />
          </TaskLink>
        )} */}
      </StyledCard>
    );
  }
  function cardContents(task, exemplar) {
    if (!exemplar) {
      // Calculate total marks and parent question count
      const questions = task.questions || [];

      // Separate parent and child questions
      const parentQuestions = [];
      const childQuestionsByParent = {};

      questions.forEach(q => {
        const parent = q.parentQuestionSerialNumber ?? q.parentSerialNumber;
        if (parent != null && parent !== '') {
          const key = String(parent);
          if (!childQuestionsByParent[key]) childQuestionsByParent[key] = [];
          childQuestionsByParent[key].push(q);
        } else {
          parentQuestions.push(q);

          // Also check if question has nested children array
          if (Array.isArray(q.children) && q.children.length > 0) {
            const key = String(q.serialNumber);
            if (!childQuestionsByParent[key]) childQuestionsByParent[key] = [];
            childQuestionsByParent[key].push(...q.children);
          }
        }
      });

      const questionCount = parentQuestions.length;

      // Calculate total marks properly:
      // - If parent has marksOutOf and showMarks !== false, use that
      // - Otherwise sum child questions with showMarks !== false
      let totalMarks = 0;
      let hasAnyMarks = false;

      parentQuestions.forEach(question => {
        // Check if parent question itself has marks and showMarks is not false
        const parentMarks = question.marksOutOf ?? question.marks;

        if (parentMarks != null && parentMarks !== undefined && question.showMarks !== false) {
          // Parent has marks and showMarks is not false, use those
          totalMarks += Number(parentMarks) || 0;
          hasAnyMarks = true;
        } else {
          // Parent doesn't have marks (or showMarks is false), sum children
          const children = childQuestionsByParent[String(question.serialNumber)] || [];
          children.forEach(child => {
            // Only count child marks if showMarks is not false
            if (child.showMarks !== false) {
              const childMarks = child.marksOutOf ?? child.marks;
              if (childMarks != null && childMarks !== undefined) {
                totalMarks += Number(childMarks) || 0;
                hasAnyMarks = true;
              }
            }
          });
        }
      });

      // Format marks and questions text
      let marksAndQuestions = null;
      if (questionCount > 0) {
        marksAndQuestions = hasAnyMarks
          ? `${totalMarks} marks • ${questionCount} question${questionCount !== 1 ? 's' : ''}`
          : `${questionCount} question${questionCount !== 1 ? 's' : ''}`;
      }

      return {
        teacherId: task.teacherId,
        title: task.ownerId,
        para: task.title,
        marksAndQuestions: marksAndQuestions,
        date: task.dueAt,
        status1: isShowProgressBar(task) ? (
          <ProgressBar
            title={'Submissions'}
            isPercentage={false}
            count={actualSubmissionCount}
            total={task.expectedSubmissions}
          />
        ) : null,
        status2: isShowProgressBar(task) ? (
          <ProgressBar
            title={'Reviewed'}
            isPercentage={false}
            count={task.reviewCount}
            total={actualSubmissionCount}
          />
        ) : null,
      };
    }
    return {
      title: task.assignmentTitle,
      para: task.response,
      subTitle: "Teacher's Comment",
      subPara: task.comment,
      assignmentName: task.assignmentTitle,
    };
  }

  function formatDateWithOrdinal(dateString) {
    const date = new Date(dateString);

    const day = date.getDate();
    const ordinalSuffix = getOrdinalSuffix(day);

    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const month = monthNames[date.getMonth()];

    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${day}${ordinalSuffix} ${month} ${year} ${hours}:${minutes} ${ampm}`;
  }

  function getOrdinalSuffix(day) {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }
  const handleMore = (event, task) => {
    event.stopPropagation();
    event.preventDefault();
    // showDeletePopuphandler(task);
    setShowMoreOptions(!showMoreOptions);
  };

  const handleDelete = (event, task) => {
    event.stopPropagation();
    event.preventDefault();
    showDeletePopuphandler(task);
  };

  const handleAssignmentStatus = (event, task) => {
    event.stopPropagation();
    event.preventDefault();
    if (showAssignmentStatusPopupHandler) {
      showAssignmentStatusPopupHandler(task);
    }
  };

  const handleDateUpdate = (event, task) => {
    event.stopPropagation();
    event.preventDefault();
    showDateExtendPopuphandler(task);
  };

  const handleScheduleDateUpdate = (event, task) => {
    event.stopPropagation();
    event.preventDefault();
    showSchedulePublishPopupHandler(task);
  };

  const handleCopyLink = useCallback(() => {
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/#/tasks/${task.id}/start`;
    navigator.clipboard.writeText(url);
    toast(<Toast message="Share link copied" />);
  }, [task.id]);

  const handleShare = useCallback(() => {
    setShowSharePopup(true);
  }, []);

  const handleSharePopupClose = () => {
    setShowSharePopup(false);
  };

  const handleSharePopupSuccess = (people, successMessage) => {
    setShareSuccess({
      people,
      message: successMessage
    });
    handleSharePopupClose();
  };

  const handleShareSuccessPopupClose = () => {
    setShareSuccess(null);
  };

  const handleUploadResponses = useCallback((event) => {
    event.stopPropagation();
    event.preventDefault();
    if (props.onUploadResponses) {
      props.onUploadResponses(task);
    }
  }, [props, task]);

  const handleUploadResponsesClick = useCallback((event) => {
    event.stopPropagation();
    event.preventDefault();
    setUploadResponsesAnchorEl(event.currentTarget);
  }, []);

  const handleUploadResponsesClose = useCallback(() => {
    setUploadResponsesAnchorEl(null);
  }, []);

  const handleBulkUploadClick = useCallback((event) => {
    handleUploadResponsesClose();
    handleUploadResponses(event);
  }, [handleUploadResponses, handleUploadResponsesClose]);

  const handleStudentUploadClick = useCallback((studentId) => (event) => {
    event.stopPropagation();
    event.preventDefault();
    handleUploadResponsesClose();
    // Opens notify screen for assigned students (creates draft only on upload-on-behalf)
    window.location.href = `#/tasks/${task.id}/upload/${encodeURIComponent(studentId)}`;
  }, [task.id, handleUploadResponsesClose]);

  const handleReviewWork = useCallback((event) => {
    event.stopPropagation();
    event.preventDefault();

    window.location.href = cardHref;
  }, [cardHref]);

  const handleEditTask = useCallback((event) => {
    event.stopPropagation();
    event.preventDefault();
    // Navigate to edit task page
    window.location.href = `#/tasks/${task.id}`;
  }, [task.id]);

  const shouldShowActionButtons = () => {
    return (
      role === 'TEACHER' &&
      !exemplar &&
      !notification &&
      task.status === 'PUBLISHED'
    );
  };

  const isInReviewColumn = () => {
    // Task is in "In Review" column if it has submissions
    return task.submissionCount > 0;
  };

  const isInAssignedColumn = () => {
    // Task is in "Assigned" column if published but no submissions yet
    return task.status === 'PUBLISHED' && (task.submissionCount === 0 || !task.submissionCount);
  };

  const renderActionButtons = () => {
    if (!shouldShowActionButtons()) return null;

    // In Review column: Review work + Upload responses
    if (isInReviewColumn()) {
      return (
        <ActionButtonsContainer onClick={(e) => e.stopPropagation()}>
          <ActionButton onClick={handleReviewWork}>
            <img src="/icons/edit-purple-icon.svg" alt="" />
            Review work
          </ActionButton>
          {isShowUploadResponses(task.status) && (
            <ActionButton onClick={handleUploadResponsesClick}>
              <img src="/icons/share-download-icon.svg" alt="" />
              Upload responses
            </ActionButton>
          )}
        </ActionButtonsContainer>
      );
    }

    // Assigned column: Upload responses + Edit task
    if (isInAssignedColumn()) {
      return (
        <ActionButtonsContainer onClick={(e) => e.stopPropagation()}>
          {isShowUploadResponses(task.status) && (
            <ActionButton onClick={handleUploadResponsesClick}>
              <img src="/icons/share-download-icon.svg" alt="" />
              Upload responses
            </ActionButton>
          )}
          <ActionButton onClick={handleEditTask}>
            <img src="/icons/edit-purple-icon.svg" alt="" />
            Edit task
          </ActionButton>
        </ActionButtonsContainer>
      );
    }

    return null;
  };

  function tagsFrame(task, isOverDue, isScheduled) {
    const handleClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    if (role === 'TEACHER') {
      const classTag = {
        name: task.classTitle || '',
        tooltipTitle: task.classTitle || undefined,
      };
      return (
        <BubbleContainer>
          <TagsWrapper>
            <StatusBubbleContainer
              tags={[classTag]}
              overdue={isOverDue}
              scheduled={
                isScheduled
                  ? {
                      text: getScheduledChipText(task.publishScheduledAt),
                      tooltip: formatDateWithOrdinal(task.publishScheduledAt),
                    }
                  : null
              }
            />
          </TagsWrapper>
          {showThreeDots && (
            <DeleteButtonContainer aria-describedby={id} onClick={handleClick}>
              <IconContainer src="/icons/three-dot.svg" alt="More" />
            </DeleteButtonContainer>
          )}
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {moreOptions()}
          </Popover>
        </BubbleContainer>
      );
    }

    // For students, show subject tags with class titles on hover
    const taskSubjects = Array.isArray(task.subjects) ? task.subjects : [];
    if (task.subjectName && !taskSubjects.length) {
      taskSubjects.push(task.subjectName);
    }
    const norm = (s) => (s != null ? String(s).trim().toLowerCase() : '');
    const seenSubjects = new Set();
    const subjectTags = [];
    taskSubjects.forEach((subject) => {
      const code = typeof subject === 'string' ? subject : (subject?.subjectCode || subject?.code);
      const inlineName = typeof subject === 'object' ? (subject?.subjectName || subject?.title || '') : '';
      const subjectName = (code && subjectCodeToName.get(code)) || inlineName || code || '';
      if (!subjectName) return;
      const key = norm(code || subjectName);
      if (seenSubjects.has(key)) return;
      seenSubjects.add(key);
      subjectTags.push({ name: subjectName, tooltipTitle: task.classTitle || '' });
    });

    const tagsToShow = subjectTags.length > 0 ? subjectTags : (task.tags || []);

    if (tagsToShow.length > 0) {
      return (
        <BubbleContainer>
          <TagsWrapper>
            <StatusBubbleContainer
              tags={tagsToShow}
              overdue={isOverDue}
              scheduled={
                isScheduled
                  ? {
                      text: getScheduledChipText(task.publishScheduledAt),
                      tooltip: formatDateWithOrdinal(task.publishScheduledAt),
                    }
                  : null
              }
            />
          </TagsWrapper>
          {role === 'TEACHER' && showThreeDots && (
            <DeleteButtonContainer aria-describedby={id} onClick={handleClick}>
              <IconContainer src="/icons/three-dot.svg" alt="More" />
            </DeleteButtonContainer>
          )}
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {moreOptions()}
          </Popover>
        </BubbleContainer>
      );
    }

    return (
      <>
        {role === 'TEACHER' && showThreeDots && (
          <DeleteButtonContainerOnly>
            <DeleteButtonContainer aria-describedby={id} onClick={handleClick}>
              <IconContainer src="/icons/three-dot.svg" alt="delete" />
            </DeleteButtonContainer>
          </DeleteButtonContainerOnly>
        )}
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {moreOptions()}
        </Popover>
      </>
    );
  }

  function tagsFrameExempler(task, isOverDue) {
    const title = [];
    title.push({ name: task.classTitle });
    return (
      <BubbleContainer>
        <TagsWrapper>
          <StatusBubbleContainer tags={title} overdue={isOverDue} />
        </TagsWrapper>
      </BubbleContainer>
    );
  }

  const handlePublishAction = (e) => {
    e.stopPropagation();
    e.preventDefault();
    showPublishPopuphandler(task);
  };

  // An empty menu means either nobody is left to upload for, or the roster
  // never arrived — never let the second read as the first.
  const emptyStudentsMessage = () =>
    studentsError || rosterUnavailable ? 'Could not load students' : 'All students have submitted';

  // Upload responses submenu
  const uploadResponsesSubmenu = () => {
    const isOpen = Boolean(uploadResponsesAnchorEl);

    return (
      <Popover
        open={isOpen}
        anchorEl={uploadResponsesAnchorEl}
        onClose={handleUploadResponsesClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        style={{ marginLeft: '8px' }}
      >
        <List style={{ padding: '8px 0', minWidth: '200px' }}>
          <StyledListItem onClick={handleBulkUploadClick} style={{ color: '#1D1B20' }}>
            <IconContainer src={bulkUploadIcon} />
            <div>Bulk Upload</div>
          </StyledListItem>
          <Divider style={{ margin: '4px 0' }} />
          {loadingStudents || loadingClasses ? (
            <StyledListItem style={{ opacity: 0.6, cursor: 'wait', color: '#1D1B20' }}>
              <div>Loading students...</div>
            </StyledListItem>
          ) : students.length > 0 ? (
            students.map((student) => (
              <StyledListItem
                key={student.id}
                onClick={handleStudentUploadClick(student.id, student.name)}
                style={{ color: '#1D1B20' }}
                $noHover={true}
              >
                <div>{student.name}</div>
              </StyledListItem>
            ))
          ) : (
            <StyledListItem style={{ opacity: 0.6, color: '#1D1B20' }} $noHover={true}>
              <div>{emptyStudentsMessage()}</div>
            </StyledListItem>
          )}
        </List>
      </Popover>
    );
  };

  // action items for task card with PUBLISHED status
  const moreOptions = () => {
    const showUploadResponses =
      role === 'TEACHER' && task.status === 'PUBLISHED';

    return (
    <List style={{ padding: '4px 0' }}>
      <Tooltip title="Create a duplicate to edit" arrow placement="left">
        <StyledListItem onClick={() => assignmentIdForDuplicate(task.id)}>
          <IconContainer src="/img/Copy.svg" />
          <div>Duplicate and edit</div>
        </StyledListItem>
      </Tooltip>
      {isShowShareOption(task.status) && (
        <StyledListItem onClick={handleShare}>
          <ShareIcon style={{ fontSize: '18px' }} />
          <div>Share task</div>
        </StyledListItem>
      )}
      {showUploadResponses && (
        <StyledListItem onClick={handleUploadResponsesClick}>
          <IconContainer src="/icons/share-download-icon.svg" />
          <div>Upload responses</div>
          <ChevronRightIcon style={{ marginLeft: 'auto', fontSize: '18px' }} />
        </StyledListItem>
      )}
      {isShowCopyLink(task.status) && (
        <StyledListItem onClick={handleCopyLink}>
          <IconContainer src={CopyIcon} />
          <div>Copy link</div>
        </StyledListItem>
      )}

      {isShowChangePublishTime(task.status) && (
        <StyledListItem
          onClick={(event) => handleScheduleDateUpdate(event, task)}
        >
          <IconContainer src="/icons/clock-purple.svg" />
          <div>Change publish time</div>
        </StyledListItem>
      )}

      {isShowChangeDueTime(task.teacherId, task.status) && (
        <StyledListItem onClick={(event) => handleDateUpdate(event, task)}>
          <IconContainer src="/icons/clock-purple.svg" />
          <div>Change due time</div>
        </StyledListItem>
      )}
      {isShowCloseTaskOption(task.teacherId, task.status) && (
        <StyledListItem onClick={() => showClosePopupHandler(task.id)}>
          <IconContainer src="/icons/purple-close.svg" />
          <div>Close task</div>
        </StyledListItem>
      )}
      {isShowPublishNow(task.status) && (
        <StyledListItem onClick={handlePublishAction}>
          <IconContainer src="/icons/publish-icon.svg" />
          <div>Publish now</div>
        </StyledListItem>
      )}
      {isShowDeleteOption(task.teacherId) && (
        <StyledListItem onClick={(event) => handleDelete(event, task)} style={{ color: '#DE2B2B' }}>
          <IconContainer src="/icons/delete-logo.svg" />
          <div>Delete</div>
        </StyledListItem>
      )}
      {isShowRubricOutcomes() && (
        <StyledListItem onClick={(event) => handleAssignmentStatus(event, task)}>
          <IconContainer src="/img/rubric-outcome.svg" />
          <div>Rubric Outcomes</div>
        </StyledListItem>
      )}
      {showPublishPopup && (
        <GeneralPopup
          hidePopup={() => setShowPublishPopup(false)}
          title="Publish Task"
          textContent="Are you sure you want to publish this task?"
          buttonText="Publish"
          confirmButtonAction={publish}
        />
      )}
    </List>
    );
  };
  return (
    <>
      {createTaskCard(
        task,
        taskLink,
        refContainer,
        isSelected,
        exemplar,
        onAccept,
        onDecline
      )}
      {showSharePopup && (
        <ShareResourcePopup
          open={showSharePopup}
          onClose={handleSharePopupClose}
          items={[{
            id: task.id,
            shareType: 'task',
            name: task.title
          }]}
          onShare={handleSharePopupSuccess}
        />
      )}
      {shareSuccess && (
        <ActionPopup
          open={!!shareSuccess}
          onClose={handleShareSuccessPopupClose}
          type="share"
          message={shareSuccess.message}
          emailList={shareSuccess.people}
          primaryButtonLabel="Awesome!"
          primaryButtonAction={handleShareSuccessPopupClose}
        />
      )}
      {uploadResponsesSubmenu()}
    </>
  );
}

export default React.memo(TaskCard);
